import { useRouter } from 'next/router';

import { useCurrentPrice } from '@/hooks/api/useCurrentPrice';
import { useAccountDetails } from '@/hooks/billing/useAccountDetails';

/**
 * Hook to get the formatted subscription end date for cancelled subscriptions
 *
 * @returns {string | false} The formatted end date string if subscription is cancelled, false otherwise
 *
 * @example
 * ```tsx
 * const subEndDate = useSubEndDate();
 *
 * if (subEndDate) {
 *   return <p>Your subscription ends on {subEndDate}</p>;
 * }
 * ```
 */
export const useSubEndDate = (): string | false => {
  const { data: accountDetails } = useAccountDetails();
  const { data: currentPrice } = useCurrentPrice();
  const { locale } = useRouter();

  // Check if subscription is cancelled (auto-renew disabled)
  const autoRenew = accountDetails?.form?.subscription?.autoRenew?.value;
  const isSubscriptionCancelled = autoRenew === 0;

  // If not cancelled, return false
  if (!isSubscriptionCancelled) {
    return false;
  }

  // Get the subscription end date
  const subscriptionEndDate = currentPrice?.data?.nextBillingDate;

  // If no end date available, return false
  if (!subscriptionEndDate) {
    return false;
  }

  // Format and return the date
  const formattedDate = new Date(subscriptionEndDate).toLocaleDateString(
    locale ?? 'en-GB',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );

  return formattedDate;
};
